import { useCallback, useEffect, useState } from 'react';
import { ArrowRightLeft, UserMinus, UserPlus } from 'lucide-react';
import { getProjectRole, type ProjectFile, type UserSummary } from '@bits2schema/shared';
import { api } from '../api/client';
import { useCurrentUser } from '../auth/AuthContext';
import { useT } from '../i18n';
import { describeError } from '../utils/format';
import { ConfirmDialog } from './ConfirmDialog';

interface ProjectEditorsCardProps {
  project: ProjectFile;
  onChanged: () => void;
}

/**
 * E14-US7/US6 — správa editorů a přenos vlastnictví. Viditelné vždy (kap. 7,
 * E14-US7 AC), ale editační akce (přidat/odebrat editora, převést
 * vlastnictví) jsou k dispozici jen creatorovi — `getProjectRole` je stejná
 * čistá funkce, jakou používá i backend pro vynucení ACL.
 */
export function ProjectEditorsCard({ project, onChanged }: ProjectEditorsCardProps) {
  const t = useT();
  const currentUser = useCurrentUser();
  const isCreator = getProjectRole(project, currentUser.id) === 'creator';

  const [users, setUsers] = useState<UserSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [busy, setBusy] = useState(false);
  const [pendingTransferId, setPendingTransferId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { users: list } = await api.listUsers();
      setUsers(list);
    } catch (caught) {
      setError(describeError(caught, t));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const displayNameOf = (userId: string): string => users?.find((user) => user.id === userId)?.displayName ?? userId;

  const addableUsers = (users ?? []).filter(
    (user) => user.id !== project.creatorId && !project.editorIds.includes(user.id),
  );

  const addEditor = async () => {
    if (!selectedUserId) return;
    setBusy(true);
    setError(null);
    try {
      await api.addEditor(project.id, selectedUserId);
      setSelectedUserId('');
      onChanged();
    } catch (caught) {
      setError(describeError(caught, t));
    } finally {
      setBusy(false);
    }
  };

  const removeEditor = async (userId: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.removeEditor(project.id, userId);
      onChanged();
    } catch (caught) {
      setError(describeError(caught, t));
    } finally {
      setBusy(false);
    }
  };

  const confirmTransfer = async () => {
    if (!pendingTransferId) return;
    setBusy(true);
    setError(null);
    try {
      await api.transferOwnership(project.id, pendingTransferId);
      setPendingTransferId(null);
      onChanged();
    } catch (caught) {
      setError(describeError(caught, t));
      setPendingTransferId(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h2>{t('project.editors.title')}</h2>
      {error && <div className="notice notice--error">{error}</div>}

      <p className="hint">{t('project.editors.creatorLabel', { name: displayNameOf(project.creatorId) })}</p>

      {project.editorIds.length === 0 ? (
        <p className="hint">{t('project.editors.empty')}</p>
      ) : (
        <ul>
          {project.editorIds.map((editorId) => (
            <li key={editorId}>
              {displayNameOf(editorId)}
              {isCreator && (
                <>
                  {' '}
                  <button type="button" disabled={busy} onClick={() => void removeEditor(editorId)}>
                    <UserMinus size={16} />
                    {t('project.editors.remove')}
                  </button>
                  <button type="button" disabled={busy} onClick={() => setPendingTransferId(editorId)}>
                    <ArrowRightLeft size={16} />
                    {t('project.editors.transferButton')}
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {isCreator && (
        <div className="row" style={{ marginTop: 'var(--space-3)' }}>
          <div className="field">
            <select value={selectedUserId} onChange={(event) => setSelectedUserId(event.target.value)}>
              <option value="">{t('project.editors.addPlaceholder')}</option>
              {addableUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.displayName} ({user.username})
                </option>
              ))}
            </select>
          </div>
          <button type="button" disabled={busy || !selectedUserId} onClick={() => void addEditor()}>
            <UserPlus size={16} />
            {t('project.editors.add')}
          </button>
        </div>
      )}

      {pendingTransferId && (
        <ConfirmDialog
          title={t('project.editors.transferTitle')}
          message={t('project.editors.transferQuestion', {
            project: project.name,
            user: displayNameOf(pendingTransferId),
          })}
          confirmLabel={t('project.editors.transferConfirm')}
          confirmDisabled={busy}
          onCancel={() => setPendingTransferId(null)}
          onConfirm={() => void confirmTransfer()}
        />
      )}
    </div>
  );
}
